import { useRecoilState } from "recoil";
import { artistListState, songListState } from "../atom/atom";
import { Artist, Genre, Song } from "../types/type";
import { updateArtist, updateSongGenre } from "../api/update.api";
import { useState } from "react";

function HomePage() {
  const [songList, setSongList] = useRecoilState(songListState);
  const [totalArtistList, setTotalArtistList] = useRecoilState(artistListState);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [editedArtist, setEditedArtist] = useState<Partial<Artist>>({});
  const [hoveredArtistId, setHoveredArtistId] = useState<string | null>(null);

  const handleToggleGenre = (song: Song, genre: Genre) => {
    const updatedGenreList = song.tag.genreList.includes(genre)
      ? song.tag.genreList.filter((g) => g !== genre)
      : [...song.tag.genreList, genre];

    const updatedSong = {
      ...song,
      tag: {
        ...song.tag,
        genreList: updatedGenreList,
      },
    };

    updateSongGenre(updatedSong).then(() => {
      setSongList((prevSongs) =>
        prevSongs.map((s) => (s.songId === song.songId ? updatedSong : s))
      );
    });
  };

  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setEditedArtist({});
  };

  const handleArtistChange = (field: keyof Artist, value: any) => {
    setEditedArtist((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveArtist = async () => {
    if (!selectedArtist || Object.keys(editedArtist).length === 0) return;

    const success = await updateArtist(selectedArtist.artistId, editedArtist);

    if (success) {
      setTotalArtistList((prevList) =>
        prevList.map((artist) =>
          artist.artistId === selectedArtist.artistId
            ? { ...artist, ...editedArtist }
            : artist
        )
      );
      setSelectedArtist(null);
    }
  };

  const handleModalClose = () => {
    setSelectedArtist(null);
    setEditedArtist({});
  };

  return (
    <div style={{ padding: "24px" }}>
      <h1
        style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "24px" }}
      >
        Song and Artist Data
      </h1>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {totalArtistList.map((artist) => (
          <div
            key={artist.artistId}
            style={{
              border: "1px solid #ccc",
              margin: "10px",
              padding: "10px",
              cursor: "pointer",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              transition: "transform 0.2s",
              transform:
                hoveredArtistId === artist.artistId
                  ? "translateY(-2px)"
                  : "none",
            }}
            onMouseEnter={() => setHoveredArtistId(artist.artistId)}
            onMouseLeave={() => setHoveredArtistId(null)}
            onClick={() => handleArtistClick(artist)}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: "#f9f9f9",
                padding: "16px",
                borderRadius: "4px",
                marginBottom: "12px",
              }}
            >
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#666" }}>
                ArtistId: {artist.artistId}
              </p>
              <p
                style={{
                  margin: "4px 0",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                {artist.artistName}
              </p>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#666" }}>
                곡 수: {artist.songIdList.length}곡
              </p>
              {artist.modelName && (
                <p style={{ margin: "4px 0", fontSize: "14px", color: "#666" }}>
                  Model: {artist.modelName}
                </p>
              )}
            </div>
            <div style={{ paddingLeft: "10px" }}>
              {songList
                .filter((song) => song.artistIdList[0] === artist.artistId)
                .map((song) => (
                  <div
                    key={song.songId}
                    style={{
                      borderBottom: "1px solid #eee",
                      margin: "8px 0",
                      padding: "8px 0",
                    }}
                  >
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "12px",
                        color: "#888",
                      }}
                    >
                      {song.songId}
                    </p>
                    <p style={{ margin: "4px 0", fontSize: "14px" }}>
                      {song.title}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      {selectedArtist && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={handleModalClose}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2 style={{ fontSize: "20px", fontWeight: "bold" }}>
                아티스트 정보 수정
              </h2>
              <button
                onClick={handleModalClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  아티스트 이름
                </label>
                <input
                  value={editedArtist.artistName ?? selectedArtist.artistName}
                  onChange={(e) =>
                    handleArtistChange("artistName", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Model Name
                </label>
                <input
                  value={
                    editedArtist.modelName ?? selectedArtist.modelName ?? ""
                  }
                  onChange={(e) =>
                    handleArtistChange("modelName", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  성별
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "4px",
                  }}
                >
                  <div
                    onClick={() => handleArtistChange("isMale", true)}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor:
                        editedArtist.isMale ?? selectedArtist.isMale
                          ? "#3b82f6"
                          : "#f3f4f6",
                      color:
                        editedArtist.isMale ?? selectedArtist.isMale
                          ? "white"
                          : "black",
                    }}
                  >
                    남성
                  </div>
                  <div
                    onClick={() => handleArtistChange("isMale", false)}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: !(
                        editedArtist.isMale ?? selectedArtist.isMale
                      )
                        ? "#3b82f6"
                        : "#f3f4f6",
                      color: !(editedArtist.isMale ?? selectedArtist.isMale)
                        ? "white"
                        : "black",
                    }}
                  >
                    여성
                  </div>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  이미지 URL
                </label>
                <input
                  value={
                    editedArtist.artistImageUrl ?? selectedArtist.artistImageUrl
                  }
                  onChange={(e) =>
                    handleArtistChange("artistImageUrl", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  상세 이미지 URL
                </label>
                <input
                  value={
                    editedArtist.artistDetailImageUrl ??
                    selectedArtist.artistDetailImageUrl
                  }
                  onChange={(e) =>
                    handleArtistChange("artistDetailImageUrl", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  장르 (쉼표로 구분)
                </label>
                <input
                  value={
                    editedArtist.tag?.genreList.join(", ") ??
                    selectedArtist.tag.genreList.join(", ")
                  }
                  onChange={(e) =>
                    handleArtistChange("tag", {
                      ...selectedArtist.tag,
                      genreList: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  태그 (쉼표로 구분)
                </label>
                <input
                  value={
                    editedArtist.tag?.tagList.join(", ") ??
                    selectedArtist.tag.tagList.join(", ")
                  }
                  onChange={(e) =>
                    handleArtistChange("tag", {
                      ...selectedArtist.tag,
                      tagList: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={handleModalClose}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                취소
              </button>
              <button
                onClick={handleSaveArtist}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
